<template>
  <div class="row primary-cards">
    <div class="col-6 col-lg-3">
      <div class="dash-card">
        <div class="title">
          Registered <span class="hide-below-sm">Users</span>
        </div>
        <p class="body">All users who have signed up.</p>
        <skeleton v-if="!loadedOnce && loading" width="50px" height="32px" />
        <div v-else class="values">
          <div class="value">{{ registeredStudents }}</div>
        </div>
      </div>
    </div>

    <div class="col-6 col-lg-3">
      <div class="dash-card">
        <div class="title">Scheduled</div>
        <p class="body">Approved sessions that have not yet occurred.</p>
        <skeleton v-if="!loadedOnce && loading" width="50px" height="32px" />
        <div v-else class="values left">
          <div class="value">
            {{ scheduledSessions }}
            <span class="hide-below-sm">sessions for</span>
          </div>
          <div class="value hide-below-sm">
            {{ scheduledSessionStudents }}
            <span>user{{ scheduledSessionStudents === 1 ? '' : 's' }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="col-6 col-lg-3">
      <div class="dash-card">
        <div class="title">Credits</div>
        <p class="body">
          Remaining available credits.
        </p>
        <skeleton v-if="!loadedOnce && loading" width="50px" height="32px" />
        <div v-else class="values ">
          <div class="value align-items-baseline col-6 p-0">
            <font-awesome-icon
              :icon="['fas', 'user']"
              class="icon alt hide-above-xs"
              style="height: 0.89rem"
            />
            {{ individualCreditsRemaining }}
            <span class="ml-2 hide-below-sm">individual</span>
          </div>
          <div class="value align-items-end p-0 pl-3 col-6">
            <font-awesome-icon
              :icon="['fas', 'users']"
              class="icon alt hide-above-xs"
            />
            {{ groupCreditsRemaining }}
            <span class="ml-2 hide-below-sm">wellbeing workshop</span>
          </div>
        </div>
      </div>
    </div>

    <div class="col-6 col-lg-3">
      <div class="dash-card coverage" :class="numberOfDays < 15 && 'warning'">
        <div class="title">
          <span class="hide-below-sm">Estimated</span> Coverage
        </div>
        <p class="body">
          <skeleton v-if="!loadedOnce && loading" height="54px" />
          <template v-else>
            There are
            {{
              numberOfDays >= 180
                ? 'more than 6 months'
                : `approximately ${numberOfDays} day${
                  numberOfDays === 1 ? '' : 's'
                }`
            }}
            of credits left at the current usage rate.
          </template>
        </p>
        <skeleton v-if="!loadedOnce && loading" width="50px" height="32px" />
        <div v-else class="values">
          <div class="value">
            {{ numberOfDays >= 180 ? '180+' : numberOfDays }}
            <span>day{{ numberOfDays === 1 ? '' : 's' }}</span>
          </div>
        </div>
        <button
          class="info hide-below-sm"
          type="button"
          title="how is this calculated?"
          @click="openModal"
        >
          <font-awesome-icon
            :icon="['fas', 'question-circle']"
            class="icon alt"
            aria-hidden
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import Skeleton from './Skeleton.vue';
import CoverageDocumentationModal from './CoverageDocumentationModal.vue';
import SessionsMetricsReport from '../store/sessions-metrics-report';

export default {
  name: 'PrimaryCards',

  components: {
    Skeleton
  },

  inject: [
    'credits',
    'registrationsReport',
    'studentDetailsReport',
    'globalMetrics'
  ],
  async mounted() {
    await this.globalMetrics
      .initialize(this.$http, this.studentDetailsReport.schoolId)
      .asApprovedStatus()
      .getUpcoming();
  },

  computed: {
    loading() {
      return (
        this.credits.loading ||
        this.registrationsReport.loading ||
        this.globalMetrics.loading ||
        this.studentDetailsReport.loading
      );
    },

    loadedOnce() {
      return (
        this.credits.loadedOnce &&
        this.registrationsReport.loadedOnce &&
        this.studentDetailsReport.loadedOnce
      );
    },

    registeredStudents() {
      return this.studentDetailsReport.data.totalActiveStudents;
    },

    scheduledSessions() {
      return (
        this.globalMetrics.data.requests.approved +
        this.globalMetrics.data.requests.inProgress
      );
    },

    scheduledSessionStudents() {
      return this.globalMetrics.data.uniqueStudents;
    },

    individualCreditsRemaining() {
      return this.credits.forecast.individual.remaining;
    },

    groupCreditsRemaining() {
      return this.credits.forecast.workshop.remaining;
    },

    total() {
      return this.credits.forecast.individual.total;
    },

    numberOfDays() {
      const estimate = this.credits.forecast.individual.estimatedCoverageDate;
      if(!estimate)
        return 0;
      return moment(estimate, 'YYYY-MM-DD').diff(moment(), 'day');
    }
  },

  methods: {
    openModal() {
      this.$modal.show(CoverageDocumentationModal, {
        props: {
          numberOfDays: this.numberOfDays
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.primary-cards {
  display: flex;
  color: #1d1c1c;
  margin-top: 2rem;

  @media (min-width: $md-min) {
    margin-top: 0;
  }

  .col-6 {
    @media screen and (max-width: $sm-max) {
      &:nth-child(odd) {
        padding-right: 0.25rem;
      }
      &:nth-child(even) {
        padding-left: 0.25rem;
      }
    }

    @media screen and (min-width: $lg-min) {
      display: flex;
    }
  }
}

.dash-card {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  min-height: 50px;
  border-radius: 8px;
  background: #fff;
  padding: 1.25rem;
  line-height: 1.2;
  border-bottom: 1px solid #e0e0e0;
  overflow: hidden;
  margin-bottom: 0.5rem;

  @media screen and (min-width: $md-min) and (max-width: $md-max) {
    margin-bottom: 2rem;
  }

  @media screen and (min-width: $md-min) {
    min-height: 185px;
    padding: 2.25rem 2.45rem 2rem 2.45rem;
    box-shadow: 0 4px 8px -8px rgb(0 0 0 / 50%);
    border: none;
  }

  .title {
    font-size: 14px;
    font-weight: 400;
    margin: 0 0 0.5rem 0;

    @media screen and (min-width: $md-min) {
      font-size: 16px;
      font-weight: 600;
      letter-spacing: 0.01rem;
      margin: 0 0 0.75rem 0;
    }
  }

  .body {
    display: none;
    font-size: 15px;
    margin-bottom: 1rem;
    max-width: 275px;
    color: #717171;

    @media screen and (min-width: $md-min) {
      display: inherit;
    }
  }

  .values {
    display: inline-flex;
    width: 100%;
    justify-content: space-between;
    align-items: flex-end;
    flex-wrap: wrap;

    &.left {
      justify-content: flex-start;
    }
  }

  .value {
    font-weight: 700;
    font-size: 1rem;

    @media screen and (min-width: $md-min) {
      font-size: 1.8rem;
    }

    .icon {
      width: auto;
      color: #77788e;
    }

    > span {
      font-size: 1rem;
      font-weight: 400;
      color: #797979;
      margin-right: 7px;
      margin-left: 0px;
    }

    &:last-child > span {
      margin-right: 0;
    }
  }

  .info {
    position: absolute;
    right: 1.8rem;
    bottom: 2.1rem;
    background: transparent;
    border: none;
    width: 1.66rem;
    height: 1.66rem;
    border-radius: 50%;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    font-size: 1.3rem;
    color: #3c3d42;

    &:hover {
      color: #fff;
    }
  }
}

.coverage {
  background: #4bf48f;
  background: linear-gradient(165deg, #4bf48f 0%, #60d9d1 100%);

  .body,
  .value > span {
    color: #555;
  }

  &.warning {
    background: #ffd069;
    background: linear-gradient(175deg, #ffd069 0%, #ffeb78 100%);

    .body {
      color: inherit;
    }
  }
}
</style>
